import ReactMarkdown from 'react-markdown';
import React, { useState, useEffect, forwardRef } from 'react';
import { HashRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Map, 
  Calendar, 
  Users, 
  User,
  ShieldCheck, 
  Clock, 
  DollarSign, 
  CheckCircle2, 
  MessageSquare, 
  X, 
  Menu,
  Phone,
  Mail,
  ArrowRight,
  ArrowLeft,
  ClipboardList,
  Zap,
  LayoutDashboard,
  Quote,
  Home,
  Building,
  Target,
  Eye,
  FileText,
  Shield,
  Briefcase,
  ChevronRight,
  ChevronDown,
  Instagram,
  Bot,
  Send,
  Minus,
  Loader2,
  Paperclip,
  Globe,
  Award,
  TrendingUp,
  Link as LinkIcon,
  Truck,
  Box,
  Trash2,
  Settings,
  Star,
  Compass,
  Activity,
  Layers,
  BarChart3,
  Network,
  Plus,
  Heart,
  Coffee,
  Laptop,
  MapPin,
  UploadCloud,
  Check
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Global Animation Settings ---
const transitionProps = { duration: 0.4, ease: [0.22, 1, 0.36, 1] };

// --- Global Service Data ---
const serviceData = {
  'residential': { 
    title: "Residential Moving", 
    icon: Home, 
    tagline: "Your family's seamless transition starts here.",
    description: "Complete planning for household moves. We handle the transition details so you can focus on your family.", 
    deepDiveContent: "Moving your entire life from one home to another is consistently ranked as one of the most stressful life events. Our residential move coordination strips away the anxiety. We act as your personal moving concierge, engineering a custom transition plan that accounts for everything from delicate heirlooms to strict HOA guidelines and tight closing schedules.",
    features: [
      { title: "Inventory Assessment", desc: "Thorough virtual or in-person evaluation to precisely determine truck sizes and labor requirements." }, 
      { title: "Verified Movers", desc: "We source and book only fully insured, rigorously vetted, and highly-rated local moving crews." }, 
      { title: "Timeline Management", desc: "Flawless coordination of lease end dates, closing dates, and utility transfers." }, 
      { title: "Specialty Care", desc: "Expert handling arrangements for pianos, fine art, antiques, and high-value electronics." }
    ],
    benefits: ["Zero hidden vendor fees", "Vetted, premium labor", "Dedicated move day support", "Flexible scheduling"],
    process: [
      { step: "Initial Audit", detail: "We evaluate your household inventory and specific transition requirements." },
      { step: "Vendor Matching", detail: "Pairing you with the perfect, vetted moving crew for your exact items and budget." },
      { step: "Move Execution", detail: "Live tracking, active communication, and issue resolution on the big day." }
    ]
  },
  'office': { 
    title: "Office Relocation", 
    icon: Building, 
    tagline: "Zero operational lag. Maximum efficiency.",
    description: "Minimizing downtime for business moves. Monday morning ready with zero operational lag.", 
    deepDiveContent: "Corporate relocations are complex logistical puzzles where every hour of downtime equals lost revenue. SmoothRoute specializes in phased, highly-orchestrated office transitions. We ensure your IT infrastructure, sensitive documents, and workstation setups are securely transported and operational exactly when you need them.",
    features: [
      { title: "IT Coordination", desc: "Meticulous disconnect and reconnect planning for servers, workstations, and sensitive tech." }, 
      { title: "Phased Schedules", desc: "Strategic moving in stages (nights or weekends) to keep your core operations running uninterrupted." }, 
      { title: "Building Compliance", desc: "Seamless handling of COI (Certificate of Insurance) requirements, loading dock reservations, and property management rules." }, 
      { title: "Staff Support", desc: "Clear, structured instructions and packing guidelines provided directly to your employees." }
    ],
    benefits: ["Minimized downtime", "IT infrastructure protection", "COI & Compliance handling", "Employee transition guides"],
    process: [
      { step: "Scope & Strategy", detail: "Detailed departmental audits and milestone planning to ensure zero operational lag." },
      { step: "Logistics Booking", detail: "Securing commercial-grade movers, specialized IT handlers, and secure transport." },
      { step: "Phased Rollout", detail: "Executing the move in coordinated waves to maintain continuous business functionality." }
    ]
  },
  'route': { 
    title: "Route Planning", 
    icon: Map, 
    tagline: "The mathematics of a faster, cheaper move.",
    description: "Intelligent logistics to reduce move times and hourly labor costs.", 
    deepDiveContent: "In the moving industry, time is literally money. An inefficient route or unexpected delay translates directly to higher hourly labor fees. SmoothRoute utilizes advanced, AI-driven spatial analysis to chart the mathematically shortest and most time-efficient routes for your moving trucks, factoring in variables most DIY movers miss.",
    features: [
      { title: "Traffic Analysis", desc: "Dynamic routing to avoid rush hour bottlenecks, construction zones, and known delays." }, 
      { title: "Permit Logistics", desc: "We secure necessary municipal parking permits, street block-offs, and loading zone reservations." }, 
      { title: "Efficient Loading", desc: "Strategic truck packing plans sequenced for faster delivery and unloading at the destination." }, 
      { title: "Distance Mapping", desc: "Advanced algorithmic routing for long-distance hauls to minimize fuel surcharges and transit days." }
    ],
    benefits: ["Reduced hourly labor costs", "Avoidance of parking fines", "Optimized fuel efficiency", "Precise delivery windows"],
    process: [
      { step: "Spatial Analysis", detail: "Mapping exact origins and destinations, accounting for truck height/weight restrictions." },
      { step: "Permit Procurement", detail: "Liaising with city municipalities to secure legal loading zones and street access." },
      { step: "Route Optimization", detail: "Delivering a turn-by-turn logistical itinerary to the motor carrier for maximum speed." }
    ]
  },
  'vendor': { 
    title: "Vendor Management", 
    icon: Users, 
    tagline: "Only the industry's finest, heavily vetted.",
    description: "We don't own trucks; we find the best providers and manage them for you.", 
    deepDiveContent: "The moving industry is unfortunately fraught with scams, hidden fees, and unreliable operators. SmoothRoute serves as your protective shield. Because we don't own trucks, we remain completely neutral, fiercely advocating for you to source, vet, and book only the most reputable, licensed motor carriers and service providers.",
    features: [
      { title: "Price Negotiation", desc: "Expert review of binding vs. non-binding quotes to negotiate the best rates and prevent hidden fees." }, 
      { title: "Credential Checks", desc: "Strict, multi-point verification of FMCSA licenses, active insurance policies, and safety records." }, 
      { title: "Auxiliary Services", desc: "Comprehensive booking for junk removal, deep cleaning, temporary storage, and vehicle transport." }, 
      { title: "Active Advocacy", desc: "Serving as your primary point of contact to resolve any disputes or logistical hiccups on move day." }
    ],
    benefits: ["Scam prevention", "Neutral, unbiased selection", "Bulk-rate negotiations", "Single point of contact"],
    process: [
      { step: "Network Sourcing", detail: "Tapping into our private alliance of top-tier carriers and local service providers." },
      { step: "Rigorous Vetting", detail: "Cross-referencing state/federal databases for safety, insurance, and performance history." },
      { step: "Contract Locking", detail: "Finalizing ironclad agreements that protect your budget and timeline from hidden surprises." }
    ]
  }
};


// --- Components ---

const PageWrapper = forwardRef(({ children, backPath, backText }, ref) => (
  <div
    ref={ref}
    className="pt-24 min-h-screen bg-[#F8FAFC] flex flex-col"
  >
    {backPath && backText && (
      <div className="max-w-7xl mx-auto px-6 w-full pt-8 md:pt-12 -mb-4 md:-mb-8 relative z-20">
        <Link
          to={backPath}
          className="group inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-[#003366] transition-all cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 text-slate-400 group-hover:text-[#003366] group-hover:-translate-x-1.5 transition-transform" strokeWidth={2} /> 
          Return to {backText}
        </Link>
      </div>
    )}
    {children}
  </div>
));

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesHovered, setIsServicesHovered] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const activePath = location.pathname;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/', id: 'home' },
    { name: 'Services', path: '/services', id: 'services' },
    { name: 'Pricing', path: '/pricing', id: 'pricing' },
    { name: 'Our Story', path: '/about', id: 'about' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 flex justify-center ${isScrolled ? 'top-0 px-0 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm py-3' : 'top-0 px-0 bg-transparent py-6'}`}>
      <div className="w-full max-w-7xl px-6 flex items-center justify-between transition-all duration-300">
        
        <Link to="/" className="flex items-center cursor-pointer group">
          <motion.img 
            src="https://res.cloudinary.com/dnhn5r2gn/image/upload/v1774641237/Logo_tra_df0zpc.png" 
            alt="SmoothRoute Logo" 
            className="h-10 md:h-12 w-auto object-contain origin-left"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <div 
              key={link.id}
              className="relative flex items-center h-full py-2"
              onMouseEnter={() => link.id === 'services' && setIsServicesHovered(true)}
              onMouseLeave={() => link.id === 'services' && setIsServicesHovered(false)}
            >
              <Link
                to={link.path}
                className={`text-sm font-semibold tracking-wide transition-colors flex items-center gap-1.5 ${activePath === link.path || (link.id === 'services' && activePath.startsWith('/services')) ? 'text-[#003366]' : 'text-slate-600 hover:text-[#003366]'}`}
              >
                {link.name}
                {link.id === 'services' && (
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isServicesHovered ? 'rotate-180' : ''}`} />
                )}
              </Link>

              {link.id === 'services' && (
                <AnimatePresence>
                  {isServicesHovered && (
                    <motion.div
                      initial={{ opacity: 0, y: 5, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 5, scale: 0.98 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-4 z-50"
                    >
                      <div className="w-64 bg-white border border-slate-200 shadow-xl rounded-xl py-2 overflow-hidden relative">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#003366] to-[#14b8a6]"></div>
                        {[
                          { path: '/services/residential', name: 'Residential Moving', icon: Home },
                          { path: '/services/office', name: 'Office Relocation', icon: Building },
                          { path: '/services/route', name: 'Route Planning', icon: Map },
                          { path: '/services/vendor', name: 'Vendor Booking', icon: Users },
                        ].map((subLink) => (
                          <Link
                            key={subLink.path}
                            to={subLink.path}
                            onClick={() => setIsServicesHovered(false)}
                            className={`w-full text-left px-5 py-3 text-sm transition-colors flex items-center gap-3 hover:bg-slate-50 ${activePath === subLink.path ? 'text-[#003366] font-semibold bg-slate-50' : 'text-slate-600 font-medium hover:text-[#003366]'}`}
                          >
                            <subLink.icon className={`w-[18px] h-[18px] ${activePath === subLink.path ? 'text-[#14b8a6]' : 'text-slate-400'}`} strokeWidth={1.5} />
                            {subLink.name}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          ))}
          <div className="w-px h-5 bg-slate-300 mx-2"></div>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-[#0a192f] text-white px-6 py-2.5 rounded-md text-sm font-semibold hover:bg-[#003366] transition-colors flex items-center gap-2 shadow-sm hover:shadow-md"
          >
            Get Started <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <button className="md:hidden text-slate-900" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="absolute top-[60px] left-0 right-0 bg-white border-b border-slate-200 shadow-lg md:hidden flex flex-col px-6 py-4 max-h-[80vh] overflow-y-auto"
          >
            {navLinks.map((link) => (
              <div key={link.id} className="border-b border-slate-100 last:border-0 flex flex-col">
                <div className="flex items-center justify-between">
                  <Link
                    to={link.path}
                    onClick={() => {
                      if (link.id !== 'services') setIsMobileMenuOpen(false);
                    }}
                    className={`text-left text-lg font-semibold py-3 flex-1 ${activePath === link.path || (link.id === 'services' && activePath.startsWith('/services')) ? 'text-[#003366]' : 'text-slate-900'}`}
                  >
                    {link.name}
                  </Link>
                  {link.id === 'services' && (
                    <button 
                      onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                      className="p-2 text-slate-400 hover:text-slate-900"
                    >
                      <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                
                {link.id === 'services' && (
                  <AnimatePresence>
                    {isMobileServicesOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="flex flex-col gap-1 pb-4 pl-4 mt-1">
                          {[
                            { path: '/services/residential', name: 'Residential Moving' },
                            { path: '/services/office', name: 'Office Relocation' },
                            { path: '/services/route', name: 'Route Planning' },
                            { path: '/services/vendor', name: 'Vendor Booking' },
                          ].map((subLink) => (
                            <Link
                              key={subLink.path}
                              to={subLink.path}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`block w-full text-left text-sm font-medium py-2.5 border-l-2 pl-4 transition-colors ${activePath === subLink.path ? 'border-[#14b8a6] text-[#003366]' : 'border-slate-200 text-slate-500 hover:border-[#14b8a6] hover:text-slate-900'}`}
                            >
                              {subLink.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
            <button 
              onClick={() => { navigate('/contact'); setIsMobileMenuOpen(false); }}
              className="mt-6 w-full bg-[#0a192f] text-white p-3.5 rounded-md text-center font-semibold text-sm shadow-sm"
            >
              Book Consultation
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const SectionHeading = ({ title, subtitle, centered = true }) => (
  <div className={`mb-12 md:mb-16 ${centered ? 'text-center' : ''}`}>
    <motion.h2 
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={transitionProps}
      className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0a192f] mb-4 tracking-tight"
    >
      {title}
    </motion.h2>
    {subtitle && (
      <motion.p 
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ ...transitionProps, delay: 0.1 }}
        className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto font-normal leading-relaxed"
      >
        {subtitle}
      </motion.p>
    )}
  </div>
);

const Hero = () => {
  const navigate = useNavigate();
  return (
    <section className="relative pt-12 pb-16 md:pb-24 overflow-hidden bg-[#F8FAFC]">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={transitionProps}
          className="lg:col-span-7 pt-4 md:pt-8"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-white text-[10px] md:text-xs font-bold tracking-widest uppercase text-[#003366] mb-6 md:mb-8 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[#14b8a6] animate-pulse"></span>
            Intelligent, Efficient Logistics
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] font-bold text-[#0a192f] leading-[1.1] mb-6 tracking-tight">
            Smarter Moves. <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#003366] to-[#14b8a6]">Smoother Routes.</span>
          </h1>
          
          <p className="text-base md:text-xl text-slate-600 mb-8 md:mb-10 leading-relaxed max-w-2xl font-normal">
            We architect, coordinate, and optimize your transition logistics, reducing operational downtime, manual friction, and hidden costs.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-[#003366] text-white px-6 py-3.5 md:px-8 md:py-4 rounded-md font-semibold text-sm md:text-base hover:bg-[#0a192f] transition-all shadow-md flex items-center justify-center gap-3"
            >
              Start Your Plan <ArrowRight className="w-5 h-5" strokeWidth={2} />
            </button>
            <button 
               onClick={() => navigate('/how-it-works')}
              className="bg-white text-[#0a192f] border border-slate-300 px-6 py-3.5 md:px-8 md:py-4 rounded-md font-semibold text-sm md:text-base hover:bg-slate-50 transition-all flex items-center justify-center gap-2 shadow-sm"
            >
              See How It Works
            </button>
          </div>
          
          <div className="mt-10 md:mt-12 pt-6 md:pt-8 border-t border-slate-200 flex items-center gap-4 md:gap-6">
            <div className="flex -space-x-3">
             {[1,2,3].map(i => (
               <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 shadow-sm">
                 0{i}
               </div>
             ))}
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest leading-tight">
            Trusted by modern <br />enterprises & families.
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="lg:col-span-5 relative mt-8 lg:mt-0"
      >
        <div className="relative z-10 w-full max-w-lg mx-auto lg:mr-0">
          <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-xl relative overflow-hidden">
            {/* Static Top Bar */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#003366] to-[#14b8a6]"></div>

            <div className="flex items-center justify-between mb-8">
               <div>
                 <h3 className="text-[10px] font-bold tracking-widest uppercase text-[#003366] mb-1">The SmoothRoute Advantage</h3>
                 <p className="text-sm sm:text-base font-bold text-slate-900 tracking-tight leading-none">Why we're different</p>
               </div>
               <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center border border-slate-200">
                 <ShieldCheck className="w-5 h-5 text-[#14b8a6]" strokeWidth={2} />
               </div>
            </div>

            <div className="space-y-3">
               {[
                 { title: 'Focus', sr: 'Logistics Planning', trad: 'Manual Labor Only', icon: Map },
                 { title: 'Pricing', sr: 'Upfront & Transparent', trad: 'Hidden Fees', icon: DollarSign },
                 { title: 'Process', sr: 'Vendor Management', trad: 'DIY Coordination', icon: Users },
                 { title: 'Routing', sr: 'Advanced Optimization', trad: 'Basic or None', icon: Zap },
               ].map((item, i) => (
                 <div 
                  key={i} 
                  className="group relative p-4 rounded-xl bg-slate-50 hover:bg-white border border-slate-200 hover:border-[#14b8a6]/50 transition-all duration-300 flex items-center justify-between hover:shadow-md overflow-hidden cursor-default"
                >
                   {/* Hover Accent Line */}
                   <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#14b8a6] -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-out"></div>
                   
                   <div className="flex items-center gap-4 relative z-10">
                     <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-white shadow-sm border border-slate-200 flex items-center justify-center shrink-0 group-hover:bg-[#003366] group-hover:border-[#003366] transition-all duration-300">
                       <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#003366] group-hover:text-white transition-colors duration-300" strokeWidth={2} />
                     </div>
                     <div>
                       <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">{item.title}</p>
                       <p className="text-sm sm:text-base font-bold text-[#0a192f] group-hover:text-[#003366] transition-colors">{item.sr}</p>
                     </div>
                   </div>
                   
                   {/* Traditional Comparison on the right */}
                   <div className="text-right pl-4 border-l border-slate-200 hidden sm:block relative z-10">
                     <p className="text-[8px] sm:text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Traditional</p>
                     <p className="text-[10px] sm:text-xs font-semibold text-slate-500 line-through decoration-slate-400">{item.trad}</p>
                   </div>
                 </div>
               ))}
            </div>
          </div>
        </div>
      </motion.div>

      </div>
    </section>
  )
};

const WhatWeDo = () => (
  <section className="py-16 md:py-24 bg-white border-y border-slate-200">
    <div className="max-w-7xl mx-auto px-6">
      <SectionHeading 
        title="We engineer your move."
        subtitle="Moving companies provide the muscle. We provide the intelligence. Enjoy a structured, risk-mitigated logistics experience managed entirely by professionals."
      />
      
      <div className="grid lg:grid-cols-3 gap-6">
        {[
          { title: 'Architect the Plan', icon: LayoutDashboard, desc: 'We formulate a precise step-by-step strategy based on your timeline, spatial constraints, and budget.' },
          { title: 'Secure Vendors', icon: Target, desc: 'We source, strictly vet, and contract the most reliable commercial and residential moving crews in our network.' },
          { title: 'Execute Logistics', icon: Network, desc: 'We coordinate schedules, optimize transit routes, and oversee all communications to ensure operational continuity.' }
        ].map((item, i) => (
          <motion.div 
            key={i} 
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
            className="p-8 md:p-10 bg-[#F8FAFC] rounded-2xl border border-slate-200 hover:border-[#14b8a6]/30 hover:shadow-lg transition-all duration-300 flex flex-col group cursor-default"
          >
            <div className="w-14 h-14 bg-white rounded-xl border border-slate-200 flex items-center justify-center mb-6 group-hover:bg-[#003366] transition-colors duration-300 shadow-sm">
              <item.icon className="w-6 h-6 text-[#003366] group-hover:text-white transition-colors duration-300" strokeWidth={2} />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-[#0a192f] mb-3 tracking-tight">{item.title}</h3>
            <p className="text-sm md:text-base text-slate-600 font-normal leading-relaxed">{item.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const HowItWorks = () => {
  const [activeStep, setActiveStep] = useState(0);
  
  const steps = [
    {
      id: "01",
      title: "Discovery Phase",
      desc: "A focused consultation to evaluate your transition scope, timeline, specialty items, and budgetary parameters to establish a logistical baseline.",
      features: ["Requirement Audit", "Constraint Analysis", "Cost Projections"],
      icon: Phone
    },
    {
      id: "02",
      title: "Strategy Formulation",
      desc: "Our planners engineer a comprehensive routing and scheduling strategy. We deliver a concrete timeline outlining every milestone and vendor dependency.",
      features: ["Route Optimization", "Milestone Mapping", "Compliance Checks"],
      icon: LayoutDashboard
    },
    {
      id: "03",
      title: "Vendor Procurement",
      desc: "We leverage our private network to bid, vet, and contract licensed motor carriers and auxiliary services, ensuring maximum reliability and cost-efficiency.",
      features: ["Credential Verification", "Contract Negotiation", "Service Locking"],
      icon: Users
    },
    {
      id: "04",
      title: "Active Execution",
      desc: "Live coordination during transition day. We track assets, resolve on-site friction, and ensure quality control until the final item is placed.",
      features: ["Live Tracking", "Friction Resolution", "Quality Assurance"],
      icon: Activity
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-[#0a192f] text-white relative">
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 mix-blend-overlay"></div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-slate-700 bg-slate-800 text-[10px] font-bold tracking-widest uppercase text-[#14b8a6] mb-4">
            <Zap className="w-3.5 h-3.5" /> Step-by-Step Guide
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight text-white">The Process.</h2>
          <p className="text-slate-400 text-base md:text-lg max-w-2xl font-normal leading-relaxed">
            A structured, four simple steps to a flawlessly executed move. Transparent, easy to understand, and completely managed for you.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Interactive Sidebar */}
          <div className="lg:col-span-5 flex flex-col gap-3">
            {steps.map((step, idx) => (
              <button
                key={idx}
                onClick={() => setActiveStep(idx)}
                className={`text-left p-5 rounded-xl border transition-all duration-300 flex items-center gap-4 ${activeStep === idx ? 'bg-[#003366] border-[#14b8a6] shadow-lg' : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800 hover:border-slate-600'}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border ${activeStep === idx ? 'bg-[#0a192f] border-[#14b8a6] text-[#14b8a6]' : 'bg-slate-700 border-slate-600 text-slate-400'}`}>
                  <step.icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <div>
                  <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${activeStep === idx ? 'text-[#14b8a6]' : 'text-slate-500'}`}>Phase {step.id}</p>
                  <h3 className={`font-bold text-base md:text-lg ${activeStep === idx ? 'text-white' : 'text-slate-300'}`}>{step.title}</h3>
                </div>
                <ChevronRight className={`w-5 h-5 ml-auto transition-transform ${activeStep === idx ? 'text-[#14b8a6] translate-x-1' : 'text-slate-600 opacity-0'}`} />
              </button>
            ))}
          </div>

          {/* Dynamic Content Console */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="h-full bg-slate-900 border border-slate-700 rounded-2xl p-8 md:p-12 relative overflow-hidden flex flex-col"
              >
                {/* Tech Accents */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#14b8a6]/10 rounded-full blur-3xl"></div>
                <div className="absolute top-6 right-6 font-mono text-slate-700 text-xs">SEQ_0{activeStep + 1}</div>
                
                <div className="w-16 h-16 bg-slate-800 border border-slate-700 rounded-xl flex items-center justify-center mb-8 relative z-10 shadow-inner">
                   {React.createElement(steps[activeStep].icon, { className: "w-8 h-8 text-[#14b8a6]", strokeWidth: 1.5 })}
                </div>
                
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight relative z-10">{steps[activeStep].title}</h3>
                <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-10 relative z-10 flex-1">
                  {steps[activeStep].desc}
                </p>

                <div className="relative z-10">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Core Deliverables</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {steps[activeStep].features.map((feat, i) => (
                      <div key={i} className="flex items-center gap-3 bg-slate-800/80 border border-slate-700 px-4 py-3 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 text-[#14b8a6] shrink-0" strokeWidth={2} />
                        <span className="text-sm font-semibold text-slate-300">{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

const Pricing = ({ showEstimator = true }) => {
  const [activeTab, setActiveTab] = useState(1);
  const navigate = useNavigate();
  
  const handleSelectPlan = (planName) => {
    navigate('/contact', { state: { plan: planName } });
  };

  const calculatorData = [
    { id: 0, label: 'Apartment', hours: 12, vendors: 2, hiddenFees: '$150+', complexity: 20 },
    { id: 1, label: 'House (3+ Bed)', hours: 25, vendors: 3, hiddenFees: '$400+', complexity: 45 },
    { id: 2, label: 'Large Estate', hours: 45, vendors: 5, hiddenFees: '$900+', complexity: 75 },
    { id: 3, label: 'Corporate Office', hours: 80, vendors: 6, hiddenFees: '$1,500+', complexity: 95 }
  ];

  const currentCalc = calculatorData[activeTab];

  return (
    <section className="py-16 md:py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeading 
          title="Transparent Pricing Architecture."
          subtitle="Professional coordination with clear, upfront costs. Our structured planning prevents hidden fees, effectively paying for itself."
        />

        {/* Crisp SaaS Pricing Tiers */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-16 md:mb-24">
          {[
            { name: 'Free Intro Call', price: 'Free', desc: 'A quick consultation to evaluate your logistical needs.', features: ['Scope Assessment', 'Cost Projections', 'Logistics Advice'], cta: 'Schedule Call', value: 'Free Intro Call' },
            { name: 'Basic Framework', price: '$39.99', desc: 'Essential checklists and structured recommendations.', features: ['Step-by-Step Guide', 'Verified Vendor List', 'Milestone Checklist'], cta: 'Get Basic', value: 'Basic Plan' },
            { name: 'Standard Coordination', price: '$99.99', suffix: 'Starting at', desc: 'Complete end-to-end planning management.', features: ['Comprehensive Plan', 'Active Vendor Booking', 'Contract Review'], cta: 'Get Standard', featured: true, value: 'Standard Coordination' },
            { name: 'Premium Support', price: '$199.99', suffix: 'Starting at', desc: 'Ultimate white-glove logistical oversight.', features: ['Standard Features +', 'Live Transition Support', 'Utility Management'], cta: 'Get Premium', value: 'Premium Move-Day Support' }
          ].map((plan, i) => (
            <motion.div 
              key={i} 
              whileHover={{ y: -5 }}
              transition={{ duration: 0.2 }}
              className={`p-8 rounded-2xl border flex flex-col relative transition-all duration-300 ${plan.featured ? 'bg-white border-[#003366] shadow-xl scale-100 lg:scale-105 z-10' : 'bg-white border-slate-200 shadow-sm hover:shadow-md'}`}
            >
              {plan.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#003366] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-sm">Recommended</div>}
              
              <h3 className="text-xl font-bold mb-2 tracking-tight text-[#0a192f] mt-2">{plan.name}</h3>
              <p className="mb-6 text-slate-500 font-normal text-sm h-10">{plan.desc}</p>
              
              <div className="mb-8 pb-8 border-b border-slate-100">
                {plan.suffix && <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{plan.suffix}</div>}
                <span className="text-4xl font-bold tracking-tight text-[#0a192f]">{plan.price}</span>
              </div>
              
              <ul className="space-y-4 mb-8 flex-1">
                {plan.features.map((feat, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm font-medium text-slate-700">
                    <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${plan.featured ? 'text-[#14b8a6]' : 'text-slate-300'}`} strokeWidth={2} />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
              
              <button onClick={() => handleSelectPlan(plan.value)} className={`w-full py-3.5 rounded-md font-bold text-sm transition-all border ${plan.featured ? 'bg-[#003366] text-white border-[#003366] hover:bg-[#0a192f]' : 'bg-white text-[#0a192f] border-slate-300 hover:border-[#0a192f] hover:bg-slate-50'}`}>
                {plan.cta}
              </button>
            </motion.div>
          ))}
        </div>

        {/* High-Tech Terminal ROI Estimator */}
        {showEstimator && (
        <div className="max-w-5xl mx-auto bg-[#0a192f] rounded-2xl border border-slate-800 shadow-2xl relative overflow-hidden flex flex-col">
          {/* Terminal grid bg */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:32px_32px]"></div>
          
          <div className="p-8 md:p-10 relative z-10">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 pb-6 border-b border-slate-800">
              <div className="max-w-md">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-[#003366] text-[9px] font-bold tracking-widest uppercase text-[#14b8a6] mb-4">
                  <BarChart3 className="w-3.5 h-3.5" /> ROI Analytics Console
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">The True Cost of a DIY move.</h3>
                <p className="text-slate-400 text-sm md:text-base leading-relaxed">Select your move size to reveal the hidden logistical burden SmoothRoute takes off your plate.</p>
              </div>

              {/* Terminal Tabs */}
              <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 p-1.5 bg-slate-900 border border-slate-800 rounded-lg shrink-0">
                {calculatorData.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative px-4 py-2 rounded-md text-xs font-bold transition-all uppercase tracking-wide flex-1 text-center ${activeTab === tab.id ? 'bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/30' : 'text-slate-500 hover:text-slate-300 border border-transparent'}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Data Dashboard */}
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3 text-slate-400">
                  <Clock className="w-4 h-4" strokeWidth={2} />
                  <h4 className="text-[10px] font-bold uppercase tracking-widest">Est. Planning Deficit</h4>
                </div>
                <motion.div 
                  key={`hours-${currentCalc.id}`}
                  initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                  className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-1 font-mono"
                >
                  {currentCalc.hours}<span className="text-xl text-slate-500 ml-1">hrs</span>
                </motion.div>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">Manual Research Required</p>
              </div>

              <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3 text-slate-400">
                  <Network className="w-4 h-4" strokeWidth={2} />
                  <h4 className="text-[10px] font-bold uppercase tracking-widest">Network Complexity</h4>
                </div>
                <motion.div 
                  key={`vendors-${currentCalc.id}`}
                  initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                  className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-1 font-mono"
                >
                  {currentCalc.vendors}
                </motion.div>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">Unique Vendors to Manage</p>
              </div>

              <div className="bg-[#003366]/30 border border-[#003366] p-6 rounded-xl flex flex-col justify-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-[#14b8a6]/10 rounded-full blur-2xl"></div>
                <div className="flex items-center gap-2 mb-3 text-[#14b8a6]">
                  <DollarSign className="w-4 h-4" strokeWidth={2} />
                  <h4 className="text-[10px] font-bold uppercase tracking-widest">Financial Risk Exposure</h4>
                </div>
                <motion.div 
                  key={`fees-${currentCalc.id}`}
                  initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                  className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-1 font-mono relative z-10"
                >
                  {currentCalc.hiddenFees}
                </motion.div>
                <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-1 relative z-10">Avg. Hidden Cost Burden</p>
              </div>
            </div>

            {/* Complexity Progress */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
              <div className="flex justify-between items-end mb-3">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-slate-400" strokeWidth={2} />
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">System Entropy (Complexity Index)</span>
                </div>
                <span className="text-sm font-bold text-[#14b8a6] font-mono">{currentCalc.complexity} / 100</span>
              </div>
              <div className="h-3 w-full bg-slate-800 rounded-full overflow-hidden relative">
                <motion.div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#003366] to-[#14b8a6] rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${currentCalc.complexity}%` }}
                  transition={{ duration: 0.8, ease: "easeOut", type: "spring", bounce: 0.2 }}
                />
              </div>
            </div>

          </div>
        </div>
        )}

      </div>
    </section>
  );
};

const PartnerNetworkPage = () => {
  const navigate = useNavigate();
  const realPartners = [
    { name: "U-Haul International", category: "Fleet Equipment", desc: "North America's leading DIY moving truck and equipment rental provider.", icon: Truck },
    { name: "Three Movers", category: "Elite Labor", desc: "Premier full-service moving solutions specializing in cross-country relocations.", icon: Users },
    { name: "Penske Logistics", category: "Commercial Transport", desc: "Expert fleet management and commercial truck solutions for large operations.", icon: Globe },
    { name: "PODS®", category: "Portable Storage", desc: "Revolutionizing the storage experience with on-site and facility containers.", icon: Box },
    { name: "Budget Truck", category: "Consumer Logistics", desc: "Affordable and reliable truck rentals for residential and business moving needs.", icon: DollarSign },
    { name: "1-800-GOT-JUNK?", category: "Site Preparation", desc: "Full-service junk removal and site clearing to prepare spaces for transition.", icon: Trash2 }
  ];

  const portfolioPartners = [
    { name: "United Van Lines", category: "CARRIER", icon: Compass },
    { name: "Mayflower", category: "LOGISTICS", icon: Award },
    { name: "Atlas Van Lines", category: "GLOBAL", icon: Map },
    { name: "North American", category: "CARRIER", icon: ShieldCheck },
    { name: "Allied", category: "RELOCATION", icon: Activity },
    { name: "Two Men & Truck", category: "LOCAL", icon: Home },
    { name: "College Hunks", category: "LABOR", icon: Star },
    { name: "International Van", category: "OVERSEAS", icon: Globe },
    { name: "JK Moving", category: "PREMIUM", icon: Briefcase },
    { name: "American Van", category: "LONG HAUL", icon: Truck },
    { name: "Graebel", category: "CORPORATE", icon: Building },
    { name: "Sirva", category: "RELO", icon: Target },
  ];

  return (
    <section className="bg-[#F8FAFC] min-h-screen pb-20 md:pb-32">
      <div className="pt-16 md:pt-24 pb-32 md:pb-40 bg-[#0a192f] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(20,184,166,0.1),transparent)] opacity-50"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px]"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md border border-slate-700 bg-slate-800 text-[10px] font-bold tracking-widest uppercase text-[#14b8a6] mb-8 shadow-sm"
          >
            <Network className="w-3.5 h-3.5" /> Vendor Ecosystem
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl sm:text-6xl md:text-7xl font-bold mb-6 tracking-tight leading-tight"
          >
            Powered by <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-300 to-white">The Best in Class.</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="text-base sm:text-xl text-slate-400 font-normal max-w-3xl mx-auto leading-relaxed"
          >
            SmoothRoute bridges the gap between premium logistics planning and the most vetted, reputable motor carriers in the United States.
          </motion.p>
        </div>
      </div>

      {/* Dynamic Stats Module */}
      <div className="max-w-5xl mx-auto px-6 -mt-16 md:-mt-20 relative z-20 mb-20 md:mb-32">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-8 md:p-12 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 divide-x divide-slate-100">
           {[
             { label: "Active Vendors", val: "30+" },
             { label: "States Covered", val: "All 50" },
             { label: "Safety Rating Req.", val: "A+" },
             { label: "Carrier Scams Prevented", val: "100%" }
           ].map((stat, i) => (
             <div key={i} className="text-center px-4">
               <div className="text-3xl md:text-4xl font-bold text-[#0a192f] mb-2">{stat.val}</div>
               <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{stat.label}</div>
             </div>
           ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-20">
        <SectionHeading title="Strategic Alliance Partners" subtitle="Core operational providers integrated into our logistics framework." />
        <div className="grid md:grid-cols-3 gap-6">
          {realPartners.map((p, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all group flex flex-col"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-slate-50 rounded-lg flex items-center justify-center border border-slate-200 group-hover:bg-[#003366] transition-colors">
                  <p.icon className="w-5 h-5 text-[#0a192f] group-hover:text-white transition-colors" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#0a192f] tracking-tight leading-tight">{p.name}</h3>
                  <p className="text-[9px] font-bold text-[#14b8a6] uppercase tracking-widest mt-1">{p.category}</p>
                </div>
              </div>
              <p className="text-slate-600 font-normal leading-relaxed text-sm flex-1">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-20 md:mt-32">
        <div className="text-center mb-12">
           <h3 className="text-2xl font-bold text-[#0a192f]">Extended Carrier Network</h3>
        </div>
        
        <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-8 max-w-5xl mx-auto">
          {portfolioPartners.map((p, i) => (
            <div key={i} className="flex items-center gap-3 w-auto bg-white border border-slate-200 px-4 py-2.5 rounded-lg shadow-sm grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100 hover:-translate-y-1 cursor-default">
              <p.icon className="w-5 h-5 text-[#003366] shrink-0" strokeWidth={2} />
              <div className="flex flex-col items-start">
                <h4 className="text-sm font-bold text-slate-800 tracking-tight leading-none whitespace-nowrap">{p.name}</h4>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 md:mt-32 bg-[#003366] rounded-2xl p-10 md:p-16 text-center relative overflow-hidden border border-[#002244] shadow-2xl">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_120%,rgba(20,184,166,0.2),transparent)]"></div>
          <div className="max-w-2xl mx-auto relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">Become a Verified Vendor</h2>
            <p className="text-slate-300 text-sm md:text-base font-normal mb-8 leading-relaxed">
              We maintain strict operational standards for our partners. Apply to join our network of elite motor carriers and logistics tech companies.
            </p>
            <button 
              onClick={() => navigate('/partner-portal')}
              className="bg-[#14b8a6] text-[#0a192f] px-8 py-4 rounded-md font-bold text-sm hover:bg-white transition-all shadow-md flex items-center justify-center gap-2 mx-auto w-full sm:w-auto"
            >
              Access Partner Portal <ArrowRight className="w-4 h-4" strokeWidth={2}/>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const PartnerPortalForm = () => {
  const [submitted, setSubmitted] = useState(false);
  
  if (submitted) {
    return (
      <motion.div 
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="bg-white p-10 md:p-16 rounded-2xl border border-slate-200 text-center shadow-lg relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#003366] to-[#14b8a6]"></div>
        <div className="w-16 h-16 bg-[#14b8a6]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <ShieldCheck className="w-8 h-8 text-[#14b8a6]" strokeWidth={2} />
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-[#0a192f] mb-4 tracking-tight">Credentials Submitted.</h3>
        <p className="text-slate-600 font-normal mb-8 text-sm md:text-base max-w-md mx-auto">
          Our Compliance Team will review your operating authority and insurance records. Expect follow-up within 48 hours.
        </p>
      </motion.div>
    );
  }

  return (
    <form 
      onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }}
      className="bg-white p-8 md:p-12 rounded-2xl border border-slate-200 shadow-xl relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#003366] to-[#14b8a6]"></div>
      
      <div className="mb-10">
        <div className="flex items-center gap-2 text-[#003366] font-bold text-[10px] uppercase tracking-widest mb-3">
          <Settings className="w-3.5 h-3.5" /> Vendor Onboarding
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-[#0a192f] mb-3 tracking-tight">Partner Registration</h3>
        <p className="text-slate-500 font-normal text-sm">Provide your operational details to initiate the vetting protocol.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Legal Entity Name</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Building className="w-4 h-4 text-slate-400" strokeWidth={2} />
            </div>
            <input required type="text" className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" placeholder="e.g. Atlas Logistics Inc." />
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Primary Classification</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Truck className="w-4 h-4 text-slate-400" strokeWidth={2} />
            </div>
            <select className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-md appearance-none focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900 cursor-pointer">
              <option>Motor Carrier (Full Service)</option>
              <option>Storage / Warehousing</option>
              <option>Site Prep / Disposal</option>
              <option>Auxiliary Services</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Active Fleet Units</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Zap className="w-4 h-4 text-slate-400" strokeWidth={2} />
            </div>
            <input required type="text" className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" placeholder="Operational capacity" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">DOT / MC Number</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <ShieldCheck className="w-4 h-4 text-slate-400" strokeWidth={2} />
            </div>
            <input required type="text" className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" placeholder="Required for carriers" />
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Operations Contact</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <User className="w-4 h-4 text-slate-400" strokeWidth={2} />
            </div>
            <input required type="text" className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" placeholder="Name & Title" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Corporate Email</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="w-4 h-4 text-slate-400" strokeWidth={2} />
            </div>
            <input required type="email" className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" placeholder="contact@company.com" />
          </div>
        </div>
      </div>

      <button type="submit" className="w-full bg-[#0a192f] text-white py-4 rounded-md font-bold text-sm hover:bg-[#003366] transition-all shadow-md mt-4 flex justify-center items-center gap-2">
        Submit Application <Send className="w-4 h-4" strokeWidth={2} />
      </button>

      <p className="mt-6 text-center text-slate-400 text-[10px] uppercase tracking-widest font-bold">
        Submission implies consent to strict compliance auditing.
      </p>
    </form>
  );
};

const PartnerPortalPage = () => (
  <section className="py-24 bg-[#F8FAFC] min-h-screen">
    <div className="max-w-4xl mx-auto px-6">
      <PartnerPortalForm />
    </div>
  </section>
);

const About = () => (
  <section className="py-16 md:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-5 relative">
          <div className="aspect-[4/5] bg-slate-100 rounded-2xl overflow-hidden relative border border-slate-200 shadow-xl">
            <div className="absolute inset-0 bg-[url('https://res.cloudinary.com/dnhn5r2gn/image/upload/v1774641013/BBB2_zyllur.jpg')] bg-cover bg-center grayscale contrast-125" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a192f] via-transparent to-transparent opacity-90" />
            <div className="absolute bottom-0 left-0 w-full p-8 border-t border-white/10 backdrop-blur-sm bg-[#0a192f]/40">
              <p className="text-white font-bold text-xl tracking-tight mb-1">Bright Ojo</p>
              <p className="text-[#14b8a6] font-bold text-[10px] uppercase tracking-widest">Founder & Chief Executive Officer</p>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-slate-200 hidden lg:block">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Established</p>
            <p className="text-3xl font-bold text-[#0a192f] tracking-tighter">2025</p>
          </div>
        </div>
        <div className="lg:col-span-7 flex flex-col justify-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-slate-200 bg-slate-50 text-[10px] font-bold tracking-widest uppercase text-[#003366] mb-6 shadow-sm w-fit">
            <Globe className="w-3.5 h-3.5" /> Corporate Origins
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0a192f] mb-8 tracking-tight leading-tight">Engineered to close the logistics gap.</h2>
          
          <div className="space-y-6 text-base text-slate-600 font-normal leading-relaxed mb-12">
            <p>When the world stood still during the early days of the COVID-19 pandemic, it became painfully clear that the global logistics sector was highly vulnerable. The sudden disruption of supply chains led to operational chaos across industries. It was out of this critical realization that SmoothRoute Logistics was born.</p>
            <p>Founded by Bright Ojo, an award-winning Project Management Professional and operations management expert, SmoothRoute was created to address the everyday logistical nightmare of moving. For families relocating across the country or small businesses moving to new offices, the burden of coordination is overwhelming. We bridge that gap. By taking over the planning, finding the best movers, and managing the schedule, we transform a historically chaotic process into a smooth, organized experience.</p>
            <p>To ensure this flawless execution, we utilize data-driven decision-making and routing tools to predict disruptions and optimize routes in real-time. We focus on more than just efficiency and cost reduction; we build resilient systems. By strictly vetting vendors and engineering adaptive strategies, we safeguard our clients' peace of mind.</p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6 pt-8 border-t border-slate-200">
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
              <div className="flex items-center gap-2 mb-3"><Target className="w-4 h-4 text-[#003366]" strokeWidth={2} /><h4 className="font-bold text-[#0a192f] text-sm uppercase tracking-widest">Mission Directive</h4></div>
              <p className="text-slate-600 font-normal text-sm leading-relaxed">To deliver structured, efficient, and stress-free move coordination solutions that mathematically reduce time and capital expenditure.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
              <div className="flex items-center gap-2 mb-3"><Eye className="w-4 h-4 text-[#003366]" strokeWidth={2} /><h4 className="font-bold text-[#0a192f] text-sm uppercase tracking-widest">Strategic Vision</h4></div>
              <p className="text-slate-600 font-normal text-sm leading-relaxed">To operate as the premier logistics architect for modern relocations, building resilient supply chains resistant to unforeseen disruption.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-20 md:mt-32 bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-8 md:gap-10 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-pink-500/10 via-purple-500/5 to-orange-500/10 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/3"></div>
        <div className="max-w-xl relative z-10 text-center md:text-left">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-[10px] font-bold tracking-widest uppercase text-slate-600 mb-4 md:mb-6 shadow-sm">
            <Instagram className="w-3.5 h-3.5 text-pink-600" />
            Join The Community
          </div>
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0a192f] mb-3 md:mb-4 tracking-tight">Follow our journey on Instagram.</h3>
          <p className="text-slate-500 font-normal text-sm md:text-lg leading-relaxed">
            See behind-the-scenes of our logistics operations, get expert moving tips, and discover how we are revolutionizing resilient logistics.
          </p>
        </div>
        <div className="relative z-10 shrink-0 w-full md:w-auto mt-2 md:mt-0">
          <a 
            href="https://www.instagram.com/gosmoothroute/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="group relative flex items-center justify-center gap-3 bg-[#0a192f] text-white px-6 md:px-8 py-3.5 md:py-4 rounded-md font-bold text-sm md:text-base hover:bg-[#003366] transition-all shadow-md w-full md:w-auto"
          >
            <span>@gosmoothroute</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
          </a>
        </div>
      </div>

    </div>
  </section>
);

const ServiceDetailPage = ({ title, icon: Icon, tagline, description, deepDiveContent, features, benefits, process }) => {
  const featureIcons = [Zap, ShieldCheck, Clock, Layers];
  const navigate = useNavigate();
  
  return (
    <section className="py-12 md:py-20 bg-[#F8FAFC] min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Header Layout */}
        <div className="grid lg:grid-cols-12 gap-12 items-center mb-16 md:mb-24">
          <div className="lg:col-span-7">
            <div className="w-16 h-16 bg-white border border-slate-200 rounded-xl flex items-center justify-center mb-6 shadow-sm">
              <Icon className="w-8 h-8 text-[#003366]" strokeWidth={2} />
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#0a192f] mb-4 tracking-tight leading-tight">{title}</h1>
            <p className="text-lg md:text-xl text-[#003366] font-semibold tracking-tight mb-6">{tagline}</p>
            <p className="text-base text-slate-600 font-normal leading-relaxed mb-8 max-w-2xl">{deepDiveContent}</p>
            
            <div className="flex flex-wrap gap-3">
              {benefits?.map((benefit, bIdx) => (
                <div key={bIdx} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-md border border-slate-200 shadow-sm text-xs font-bold uppercase tracking-wide text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-[#14b8a6]" strokeWidth={2} /> {benefit}
                </div>
              ))}
            </div>
          </div>
          
          <div className="lg:col-span-5 relative hidden lg:block">
            <div className="bg-white p-10 rounded-2xl border border-slate-200 shadow-xl relative z-10">
               <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-slate-200 bg-slate-50 text-[10px] font-bold tracking-widest uppercase text-[#003366] mb-6">
                 <Activity className="w-3 h-3 text-[#14b8a6]" /> Active Support
               </div>
               <h3 className="text-2xl font-bold text-[#0a192f] mb-4 tracking-tight">Expert Coordination</h3>
               <p className="text-slate-600 font-normal leading-relaxed text-sm mb-8">We handle the heavy lifting of planning so you don't have to.</p>
               
               <div className="bg-[#F8FAFC] rounded-xl p-5 border border-slate-200">
                 <div className="flex items-center justify-between mb-4">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Planning Status</span>
                    <span className="text-xs font-bold text-[#14b8a6]">Complete</span>
                 </div>
                 <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                   <div className="h-full bg-[#003366] w-[85%] rounded-full"></div>
                 </div>
               </div>
            </div>
          </div>
        </div>

        {/* Core Capabilities */}
        <div className="mb-20">
          <h2 className="text-2xl font-bold text-[#0a192f] tracking-tight mb-8">Core Functional Parameters</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {features.map((feat, idx) => {
              const FeatIcon = featureIcons[idx % 4];
              return (
                <div key={idx} className="p-8 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-center mb-5">
                     <FeatIcon className="w-5 h-5 text-[#003366]" strokeWidth={2} />
                  </div>
                  <h3 className="text-lg font-bold text-[#0a192f] mb-2 tracking-tight">{feat.title}</h3>
                  <p className="text-sm text-slate-600 font-normal leading-relaxed">{feat.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Call */}
        <div className="bg-[#0a192f] rounded-2xl p-10 md:p-16 text-center border border-[#002244] shadow-2xl relative overflow-hidden">
           <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 mix-blend-overlay"></div>
           <div className="relative z-10 max-w-2xl mx-auto">
             <h3 className="text-3xl font-bold text-white mb-4 tracking-tight">Ready to simplify your move?</h3>
             <p className="text-slate-400 font-normal text-sm md:text-base mb-8 leading-relaxed">Let our experts take the weight off your shoulders. Schedule your free consultation today.</p>
             <button onClick={() => navigate('/contact')} className="bg-[#14b8a6] text-[#0a192f] px-8 py-4 rounded-md font-bold text-sm hover:bg-white transition-all shadow-md flex items-center justify-center gap-2 mx-auto">
               Start Your Plan <ArrowRight className="w-4 h-4" strokeWidth={2} />
             </button>
           </div>
        </div>

      </div>
    </section>
  );
};

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      q: "Are you a moving company?",
      a: "No. SmoothRoute is a logistics coordination and move management firm. We do not own trucks or employ manual laborers. Instead, we act as your dedicated project manager, engineering your transition plan and securing top-tier, vetted motor carriers to execute the physical move."
    },
    {
      q: "Why shouldn't I just book a mover directly?",
      a: "Booking directly often leaves you vulnerable to hidden fees, unvetted contractors, and logistical blind spots. We leverage industry data and a strict vetting protocol to ensure you get the best rate and the most reliable crew, while we handle the stressful administrative burden."
    },
    {
      q: "How does SmoothRoute make money?",
      a: "We charge a transparent, upfront coordination fee based on the complexity of your move (as outlined on our Pricing page). Because we negotiate bulk and partner rates with vendors, the money we save you on the actual move often offsets or exceeds our coordination fee."
    },
    {
      q: "What happens if my property is damaged during the move?",
      a: "While we only utilize highly-rated, fully insured motor carriers, physical transit carries inherent risks. If damage occurs, liability rests with the motor carrier. However, SmoothRoute acts as your advocate, assisting you in navigating the claims process to ensure a swift and fair resolution."
    },
    {
      q: "Do you handle international relocations?",
      a: "Currently, our core network is optimized for domestic (US) residential and corporate transitions. We do handle cross-border moves to Canada and Mexico on a case-by-case basis through specialized freight forwarders."
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-[#F8FAFC] min-h-screen">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-white shadow-sm text-[10px] font-bold tracking-widest uppercase text-[#003366] mb-6">
            <MessageSquare className="w-3.5 h-3.5" /> Client Knowledge Base
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0a192f] mb-6 tracking-tight">
            Frequently Asked Questions.
          </h1>
          <p className="text-slate-600 font-normal leading-relaxed text-lg">
            Clarity is the foundation of a smooth transition. Learn more about our operational model.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <div 
              key={idx} 
              className={`bg-white border rounded-2xl overflow-hidden transition-all duration-300 ${openIndex === idx ? 'border-[#003366] shadow-md' : 'border-slate-200 shadow-sm hover:border-slate-300'}`}
            >
              <button 
                onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)}
                className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none"
              >
                <span className={`font-bold text-lg tracking-tight ${openIndex === idx ? 'text-[#0a192f]' : 'text-slate-700'}`}>
                  {faq.q}
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${openIndex === idx ? 'bg-[#14b8a6]/10 text-[#14b8a6]' : 'bg-slate-50 text-slate-400'}`}>
                  {openIndex === idx ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </div>
              </button>
              <AnimatePresence>
                {openIndex === idx && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <div className="px-6 pb-6 pt-2 text-slate-600 font-normal leading-relaxed border-t border-slate-50 mt-2">
                  {faq.a}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  </div>
</section>
);
};

const jobListings = [
  { 
    id: "ops-01",
    title: "Senior Logistics Architect", 
    dept: "Operations", 
    loc: "Remote (US)", 
    type: "Full-Time",
    about: "As a Senior Logistics Architect, you will be the mastermind behind our most complex residential and corporate transitions. You will leverage spatial analysis, industry data, and your deep operational expertise to build flawless, end-to-end relocation frameworks that eliminate friction for our clients.",
    responsibilities: [
      "Design comprehensive, step-by-step relocation strategies for high-net-worth individuals and enterprise clients.",
      "Utilize proprietary routing data to map out the most cost-effective and time-efficient transit paths.",
      "Coordinate directly with the Vendor Relations team to secure specialized handling for high-value items, IT infrastructure, or fine art.",
      "Act as the primary escalation point for any live-transition friction, resolving delays or vendor disputes in real-time."
    ],
    requirements: [
      "4+ years of experience in complex logistics, supply chain management, or high-end move coordination.",
      "PMP certification or equivalent operational management experience is highly preferred.",
      "Exceptional crisis management and problem-solving skills under high-pressure scenarios.",
      "Fluency in modern CRM, spatial mapping, and logistics coordination software."
    ]
  },
  { 
    id: "part-01",
    title: "Vendor Relations Manager", 
    dept: "Partnerships", 
    loc: "Houston, TX / Hybrid", 
    type: "Full-Time",
    about: "The strength of SmoothRoute lies in the quality of our execution partners. The Vendor Relations Manager is responsible for sourcing, fiercely vetting, and continuously auditing the motor carriers, storage facilities, and service providers in our ecosystem.",
    responsibilities: [
      "Identify and onboard top-tier, licensed motor carriers across new regional markets.",
      "Conduct rigorous, multi-point credential checks including FMCSA licenses, active insurance policies, and historical safety records.",
      "Negotiate binding enterprise contracts and bulk-rate pricing to secure maximum value for our clients.",
      "Enforce SmoothRoute's strict operational compliance standards, removing underperforming vendors from the network."
    ],
    requirements: [
      "4+ years in vendor management, procurement, or B2B partnership development.",
      "Deep understanding of US motor carrier regulations, DOT compliance, and freight insurance structures.",
      "Fierce negotiation skills and a data-driven approach to performance auditing.",
      "Ability to travel up to 20% for on-site vendor evaluations."
    ]
  },
  { 
    id: "tech-01",
    title: "Route Optimization Analyst", 
    dept: "Data & Tech", 
    loc: "Remote (US)", 
    type: "Full-Time",
    about: "We believe that time is literally money in logistics. The Route Optimization Analyst turns raw geographic and traffic data into actionable, cost-saving transit itineraries for our execution teams and motor carriers.",
    responsibilities: [
      "Analyze national traffic patterns, municipal permit constraints, and seasonal weather risks to build optimized transit schedules.",
      "Develop mathematical models that predict and mitigate hourly labor overages caused by spatial inefficiencies.",
      "Assist operations teams in planning phased, multi-truck rollouts for large-scale corporate relocations.",
      "Continuously refine our internal estimation engine based on post-move data analytics."
    ],
    requirements: [
      "Degree in Supply Chain Logistics, Data Science, Mathematics, or a related analytical field.",
      "Proficiency in spatial analysis tools, SQL, and data visualization platforms (Tableau, PowerBI).",
      "Obsessive attention to detail and a passion for structural efficiency.",
      "Prior experience in freight routing or fleet management is a strong plus."
    ]
  },
  { 
    id: "sup-01",
    title: "Client Success Coordinator", 
    dept: "Support", 
    loc: "Houston, TX/ Hybrid", 
    type: "Part-Time",
    about: "Moving is inherently stressful. Our Client Success Coordinators serve as the empathetic, highly-organized face of SmoothRoute, guiding clients through their transition journey from the initial discovery call to post-move settling.",
    responsibilities: [
      "Conduct introductory discovery calls to audit client needs, constraints, and budgetary parameters.",
      "Translate complex logistics plans into clear, reassuring communication for families and business owners.",
      "Actively monitor live transitions, providing clients with real-time updates and peace of mind.",
      "Gather post-move feedback to continuously improve the SmoothRoute service methodology."
    ],
    requirements: [
      "3+ years in high-end customer success, hospitality, or account management.",
      "Impeccable written and verbal communication skills with a highly empathetic tone.",
      "Ability to comfortably translate technical logistics details to non-technical clients.",
      "High organizational capacity and familiarity with modern CRM systems (Salesforce, Hubspot)."
    ]
  }
];

const CareersPage = () => {
  const navigate = useNavigate();

  return (
    <section className="bg-[#F8FAFC] min-h-screen pb-24">
      {/* Hero */}
      <div className="pt-20 pb-24 bg-[#0a192f] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:32px_32px]"></div>
        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-700 bg-slate-800 shadow-sm text-[10px] font-bold tracking-widest uppercase text-[#14b8a6] mb-8">
            <Briefcase className="w-3.5 h-3.5" /> Join The Team
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight leading-tight">
            Build the future of <br className="hidden sm:block"/><span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-300 to-white">resilient logistics.</span>
          </h1>
          <p className="text-slate-400 font-normal text-lg max-w-2xl mx-auto leading-relaxed">
            We are looking for analytical thinkers, problem solvers, and operational experts to help us eliminate the chaos of moving.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-20 mb-20">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xl flex flex-col items-center text-center">
            <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center mb-6 border border-slate-200 text-[#003366]"><Heart className="w-6 h-6" strokeWidth={1.5} /></div>
            <h3 className="text-lg font-bold text-[#0a192f] mb-2">Comprehensive Health</h3>
            <p className="text-sm text-slate-500">Premium medical, dental, and vision coverage for you and your dependents.</p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xl flex flex-col items-center text-center">
            <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center mb-6 border border-slate-200 text-[#003366]"><Laptop className="w-6 h-6" strokeWidth={1.5} /></div>
            <h3 className="text-lg font-bold text-[#0a192f] mb-2">Remote-First Culture</h3>
            <p className="text-sm text-slate-500">Work from anywhere in the US with a generous home-office stipend.</p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xl flex flex-col items-center text-center">
            <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center mb-6 border border-slate-200 text-[#003366]"><Coffee className="w-6 h-6" strokeWidth={1.5} /></div>
            <h3 className="text-lg font-bold text-[#0a192f] mb-2">Mandatory Time Off</h3>
            <p className="text-sm text-slate-500">Unlimited PTO with a minimum 3-week requirement to ensure you recharge.</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-[#0a192f] tracking-tight mb-8">Open Positions</h2>
        <div className="space-y-4">
          {jobListings.map((job, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6 hover:shadow-lg transition-all duration-300 hover:border-[#14b8a6]/50 group">
              <div>
                <h3 className="text-xl font-bold text-[#0a192f] mb-3 group-hover:text-[#003366] transition-colors">{job.title}</h3>
                <div className="flex flex-wrap items-center gap-3 text-xs font-bold text-slate-500 uppercase tracking-widest">
                  <span className="bg-slate-100 px-3 py-1 rounded-md text-[#003366]">{job.dept}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {job.loc}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {job.type}</span>
                </div>
              </div>
              <button 
                onClick={() => navigate('/job', { state: { job } })}
                className="shrink-0 bg-white border border-slate-300 text-[#0a192f] px-6 py-3 rounded-md font-bold text-sm hover:bg-[#0a192f] hover:text-white transition-all shadow-sm flex items-center gap-2"
              >
                View Role <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const JobDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const job = location.state?.job;

  useEffect(() => {
    if (!job) {
      navigate('/careers');
    }
  }, [job, navigate]);

  if (!job) return null;

  return (
    <section className="py-12 md:py-20 bg-[#F8FAFC] min-h-screen">
      <div className="max-w-4xl mx-auto px-6">
        
        <div className="bg-white p-8 md:p-12 rounded-2xl border border-slate-200 shadow-sm mb-8">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8 border-b border-slate-100 pb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#14b8a6]/20 bg-[#14b8a6]/5 text-[10px] font-bold tracking-widest uppercase text-[#14b8a6] mb-4">
                <Layers className="w-3 h-3" /> {job.dept}
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-[#0a192f] mb-4 tracking-tight">{job.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm font-semibold text-slate-600">
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#003366]" /> {job.loc}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#003366]" /> {job.type}</span>
              </div>
            </div>
            <button 
              onClick={() => navigate('/apply', { state: { job } })}
              className="shrink-0 bg-[#0a192f] text-white px-8 py-4 rounded-md font-bold text-sm hover:bg-[#003366] transition-all shadow-md flex items-center justify-center gap-2"
            >
              Apply for this Position
            </button>
          </div>

          <div className="prose prose-slate max-w-none text-slate-600 font-normal leading-relaxed">
            <h3 className="text-xl font-bold text-[#0a192f] mb-4">About the Role</h3>
            <p className="mb-8">{job.about}</p>

            <h3 className="text-xl font-bold text-[#0a192f] mb-4">Key Responsibilities</h3>
            <ul className="space-y-3 mb-8">
              {job.responsibilities.map((resp, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#14b8a6] shrink-0 mt-0.5" strokeWidth={2} />
                  <span>{resp}</span>
                </li>
              ))}
            </ul>

            <h3 className="text-xl font-bold text-[#0a192f] mb-4">Requirements & Qualifications</h3>
            <ul className="space-y-3 mb-8">
              {job.requirements.map((req, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#003366] shrink-0 mt-2.5"></div>
                  <span>{req}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-[#0a192f] rounded-2xl p-8 md:p-12 text-center border border-[#002244] shadow-xl relative overflow-hidden">
           <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 mix-blend-overlay"></div>
           <div className="relative z-10">
             <h3 className="text-2xl font-bold text-white mb-3 tracking-tight">Ready to build with us?</h3>
             <p className="text-slate-400 font-normal text-sm md:text-base mb-8">Submit your credentials and operational history for review.</p>
             <button 
                onClick={() => navigate('/apply', { state: { job } })}
                className="bg-[#14b8a6] text-[#0a192f] px-8 py-3.5 rounded-md font-bold text-sm hover:bg-white transition-all shadow-md inline-flex items-center justify-center gap-2"
              >
               Start Application <ArrowRight className="w-4 h-4" strokeWidth={2} />
             </button>
           </div>
        </div>

      </div>
    </section>
  );
};

const JobApplicationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const job = location.state?.job || { title: 'General Application', dept: 'Operations', loc: 'Remote', type: 'Full-Time' };

  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      window.scrollTo(0, 0);
    }, 2000);
  };

  if (isSubmitted) {
    return (
      <section className="py-24 bg-[#F8FAFC] min-h-[80vh] flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl mx-auto px-6 w-full text-center"
        >
          <div className="bg-white p-12 md:p-16 rounded-2xl border border-slate-200 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#003366] to-[#14b8a6]"></div>
            <div className="w-20 h-20 bg-[#14b8a6]/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-[#14b8a6]/20">
              <CheckCircle2 className="w-10 h-10 text-[#14b8a6]" strokeWidth={2} />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a192f] mb-4 tracking-tight">Application Received.</h2>
            <p className="text-slate-600 font-normal mb-10 text-base md:text-lg leading-relaxed max-w-lg mx-auto">
              Thank you for applying to the <span className="font-bold text-[#0a192f]">{job.title}</span> position. Our Talent Acquisition team will review your credentials and contact you if your operational profile aligns with our requirements.
            </p>
            <button 
              onClick={() => navigate('/careers')}
              className="bg-slate-100 text-[#0a192f] border border-slate-200 px-8 py-3.5 rounded-md font-bold text-sm hover:bg-slate-200 transition-all"
            >
              Return to Careers
            </button>
          </div>
        </motion.div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-20 bg-[#F8FAFC] min-h-screen">
      <div className="max-w-4xl mx-auto px-6">
        
        {/* Application Header */}
        <div className="mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-white shadow-sm text-[10px] font-bold tracking-widest uppercase text-[#003366] mb-4">
            <Briefcase className="w-3 h-3" /> Candidate Portal
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-[#0a192f] mb-4 tracking-tight">{job.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-xs font-bold text-slate-500 uppercase tracking-widest">
            <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> {job.dept}</span>
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {job.loc}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {job.type}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8 md:space-y-12">
          
          {/* Resume & Documents Section */}
          <div className="bg-white p-8 md:p-10 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-[#14b8a6]"></div>
            <h3 className="text-xl font-bold text-[#0a192f] mb-6">Documents</h3>
            
            <div className="mb-8">
              <label className="block text-[10px] font-bold text-slate-500 mb-3 uppercase tracking-widest">Resume / CV *</label>
              <div className="relative">
                <input 
                  type="file" 
                  id="resume-upload" 
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx"
                  required
                />
                <div className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center text-center transition-colors ${file ? 'border-[#14b8a6] bg-[#14b8a6]/5' : 'border-slate-300 bg-slate-50 hover:bg-slate-100 group-hover:border-[#003366]'}`}>
                  {file ? (
                    <>
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm border border-[#14b8a6]/30">
                        <FileText className="w-6 h-6 text-[#14b8a6]" strokeWidth={1.5} />
                      </div>
                      <p className="text-[#0a192f] font-bold text-base mb-1">{file.name}</p>
                      <p className="text-slate-500 text-xs font-medium uppercase tracking-widest">Click to change file</p>
                    </>
                  ) : (
                    <>
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm border border-slate-200">
                        <UploadCloud className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                      </div>
                      <p className="text-[#0a192f] font-bold text-base mb-1">Click or drag file to this area to upload.</p>
                      <p className="text-slate-500 text-xs font-medium uppercase tracking-widest">PDF, DOC, DOCX (Max 5MB)</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Cover Letter (Optional)</label>
              <textarea 
                rows="5" 
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-normal text-sm text-slate-900 resize-none" 
                placeholder="Briefly explain why your background makes you a strong fit for SmoothRoute Logistics..."
              ></textarea>
            </div>
          </div>

          {/* Personal Information */}
          <div className="bg-white p-8 md:p-10 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-[#003366]"></div>
            <h3 className="text-xl font-bold text-[#0a192f] mb-6">Personal Information</h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">First Name *</label>
                <input required type="text" className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Last Name *</label>
                <input required type="text" className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Email Address *</label>
                <input required type="email" className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Phone Number *</label>
                <input required type="tel" className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">City *</label>
                <input required type="text" className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">State / Province *</label>
                <input required type="text" className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">LinkedIn Profile (Optional)</label>
                <input type="url" className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" placeholder="https://linkedin.com/in/..." />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Website / Portfolio (Optional)</label>
                <input type="url" className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" placeholder="https://..." />
              </div>
            </div>
          </div>

          {/* Professional & Logistics Profile */}
          <div className="bg-white p-8 md:p-10 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-[#0a192f]"></div>
            <h3 className="text-xl font-bold text-[#0a192f] mb-6">Professional Profile</h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Years of Logistics / Coordination Experience *</label>
                <select required className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900 appearance-none cursor-pointer">
                  <option value="">Select experience level...</option>
                  <option value="0-2">0 - 2 years</option>
                  <option value="3-5">3 - 5 years</option>
                  <option value="6-10">6 - 10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Software & CRM Expertise *</label>
                <input required type="text" className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" placeholder="e.g. Salesforce, Hubspot, Tableau, routing tools" />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Available Start Date *</label>
              <input required type="date" className="w-full md:w-1/2 px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-500" />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Desired Salary (USD) *</label>
              <input required type="text" className="w-full md:w-1/2 px-4 py-3 bg-slate-50 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" placeholder="$00,000" />
            </div>
          </div>

          {/* Work Authorization */}
          <div className="bg-white p-8 md:p-10 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-slate-400"></div>
            <h3 className="text-xl font-bold text-[#0a192f] mb-6">Work Authorization</h3>
            
            <div className="mb-8">
              <label className="block text-sm font-semibold text-slate-900 mb-4">Are you legally authorized to work in the United States? *</label>
              <div className="flex flex-col sm:flex-row gap-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="radio" name="auth" value="yes" required className="w-4 h-4 text-[#003366] focus:ring-[#003366] border-slate-300" />
                  <span className="text-sm font-medium text-slate-700">Yes</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="radio" name="auth" value="no" required className="w-4 h-4 text-[#003366] focus:ring-[#003366] border-slate-300" />
                  <span className="text-sm font-medium text-slate-700">No</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-4">Will you now or in the future require sponsorship for employment visa status (e.g., H-1B visa status)? *</label>
              <div className="flex flex-col sm:flex-row gap-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="radio" name="sponsorship" value="yes" required className="w-4 h-4 text-[#003366] focus:ring-[#003366] border-slate-300" />
                  <span className="text-sm font-medium text-slate-700">Yes</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="radio" name="sponsorship" value="no" required className="w-4 h-4 text-[#003366] focus:ring-[#003366] border-slate-300" />
                  <span className="text-sm font-medium text-slate-700">No</span>
                </label>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center md:text-left">
              Fields marked with * are required
            </p>
            <button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full md:w-auto bg-[#0a192f] text-white px-10 py-4 rounded-md font-bold text-sm hover:bg-[#003366] transition-all shadow-md disabled:opacity-70 flex items-center justify-center gap-2 min-w-[240px]"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Processing Credentials...</>
              ) : (
                <>Submit Application <Check className="w-4 h-4" strokeWidth={2} /></>
              )}
            </button>
          </div>

        </form>
      </div>
    </section>
  );
};

const TextPage = ({ title, lastUpdated, content }) => (
  <section className="py-12 bg-[#F8FAFC] min-h-screen">
    <div className="max-w-4xl mx-auto px-6">
      <div className="bg-white p-10 md:p-16 rounded-2xl border border-slate-200 shadow-sm">
        <div className="mb-10 border-b border-slate-100 pb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0a192f] mb-4 tracking-tight">{title}</h1>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Last Revision: {lastUpdated}</p>
        </div>
        <div className="prose prose-slate max-w-none text-slate-600 font-normal leading-relaxed prose-headings:font-bold prose-headings:text-[#0a192f] prose-headings:tracking-tight prose-a:text-[#003366] prose-a:font-semibold">
          {content}
        </div>
      </div>
    </div>
  </section>
);

const HowItWorksPage = () => {
  return <HowItWorks />; // Reusing the newly designed interactive component
};

const ServicesPage = ({ serviceData }) => {
  const navigate = useNavigate();
  return (
    <section className="py-16 md:py-24 bg-[#F8FAFC] min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-white shadow-sm text-[10px] font-bold tracking-widest uppercase text-[#003366] mb-6">
            <Briefcase className="w-3.5 h-3.5" /> Premium Offerings
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-[#0a192f] mb-6 tracking-tight leading-tight">
            Comprehensive Logistics Solutions.
          </h1>
          <p className="text-base md:text-lg text-slate-600 font-normal leading-relaxed">
            From household transitions to complex corporate relocations, we engineer end-to-end strategies that neutralize logistical friction.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {Object.entries(serviceData).map(([key, s], i) => (
            <div key={key} className="bg-white p-8 md:p-10 rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group relative">
              <div className="w-14 h-14 bg-slate-50 rounded-xl flex items-center justify-center mb-8 border border-slate-200 group-hover:bg-[#003366] transition-colors duration-300 shadow-sm">
                <s.icon className="w-6 h-6 text-[#003366] group-hover:text-white transition-colors" strokeWidth={2} />
              </div>
              
              <h3 className="text-2xl font-bold mb-3 tracking-tight text-[#0a192f]">{s.title}</h3>
              <p className="text-slate-600 font-normal leading-relaxed mb-8 text-sm">{s.description}</p>
              
              <div className="mb-8 flex-1">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Core Deliverables</p>
                <ul className="space-y-3">
                  {s.features.slice(0, 3).map((feat, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-slate-700 font-normal">
                      <CheckCircle2 className="w-4 h-4 text-[#14b8a6] shrink-0 mt-0.5" strokeWidth={2} />
                      <span><strong className="font-bold text-[#0a192f]">{feat.title}:</strong> {feat.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-6 border-t border-slate-100 mt-auto">
                <button onClick={() => navigate(`/services/${key}`)} className="inline-flex items-center gap-2 text-[#003366] font-bold text-sm hover:gap-3 transition-all">
                  Explore Service <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  return (
    <>
      <Hero />
      <WhatWeDo />
      <HowItWorks />
      <Pricing showEstimator={false} />
    </>
  );
};

const ContactForm = ({ initialPlan = 'Free Intro Call' }) => {
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e) => { e.preventDefault(); setSubmitted(true); };
  
  if (submitted) {
    return (
      <div className="bg-white p-10 md:p-16 rounded-2xl border border-slate-200 text-center shadow-xl relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#003366] to-[#14b8a6]"></div>
        <div className="w-16 h-16 bg-[#14b8a6]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8 text-[#14b8a6]" strokeWidth={2} />
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-[#0a192f] mb-4 tracking-tight">Request Received.</h3>
        <p className="text-slate-600 font-normal mb-8 text-sm md:text-base max-w-sm mx-auto">
          One of our coordinators will review your details and reach out within 24 hours to begin your plan.
        </p>
      </div>
    );
  }
  
  return (
    <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-2xl border border-slate-200 shadow-xl relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#003366] to-[#14b8a6]"></div>
      
      <div className="mb-10">
        <h3 className="text-2xl md:text-3xl font-bold text-[#0a192f] mb-2 tracking-tight">Request a Quote</h3>
        <p className="text-slate-500 font-normal text-sm">Tell us about your upcoming transition.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Authorized Contact</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <User className="w-4 h-4 text-slate-400" strokeWidth={2} />
            </div>
            <input required type="text" className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" placeholder="Full Name" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Communication Channel</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="w-4 h-4 text-slate-400" strokeWidth={2} />
            </div>
            <input required type="email" className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900" placeholder="Email Address" />
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Target Move Date</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Calendar className="w-4 h-4 text-slate-400" strokeWidth={2} />
            </div>
            <input required type="date" className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-500" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Service Plan</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <ClipboardList className="w-4 h-4 text-slate-400" strokeWidth={2} />
            </div>
            <select defaultValue={initialPlan} className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-md appearance-none focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900 cursor-pointer">
              <option>Free Intro Call</option>
              <option>Basic Plan</option>
              <option>Standard Coordination</option>
              <option>Premium Support</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <label className="block text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-widest">Logistics Details</label>
        <div className="relative">
          <div className="absolute top-4 left-4 flex items-start pointer-events-none">
            <Map className="w-4 h-4 text-slate-400" strokeWidth={2} />
          </div>
          <textarea rows="4" className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all font-medium text-sm text-slate-900 resize-none" placeholder="Origin, destination, and key operational constraints."></textarea>
        </div>
      </div>

      <button type="submit" className="w-full bg-[#0a192f] text-white py-4 rounded-md font-bold text-sm hover:bg-[#003366] transition-all shadow-md flex justify-center items-center gap-2">
        Send Request <Send className="w-4 h-4" strokeWidth={2} />
      </button>
    </form>
  );
};

const ContactPage = () => {
  const location = useLocation();
  const initialPlan = location.state?.plan || 'Free Intro Call';

  return (
    <section className="py-20 md:py-32 bg-[#F8FAFC] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5 pt-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-slate-200 bg-white text-[10px] font-bold tracking-widest uppercase text-[#003366] mb-6 shadow-sm">
            <ShieldCheck className="w-3 h-3 text-[#14b8a6]" /> Priority Support
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight text-[#0a192f] leading-tight">
            Let's plan your move.
          </h2>
          <p className="text-base text-slate-600 mb-12 font-normal leading-relaxed">
            Whether you're moving across town or relocating your office, our team is ready to engineer a flawless transition.
          </p>
          
          <div className="space-y-4 mb-12">
            <div className="flex items-center gap-4 p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
              <div className="w-12 h-12 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 text-[#003366]" strokeWidth={2} />
              </div>
              <div>
                <h4 className="font-bold text-slate-400 text-[10px] uppercase tracking-widest mb-1">Direct Line</h4>
                <p className="text-[#0a192f] font-bold text-lg tracking-tight">(832) 409-0565</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
              <div className="w-12 h-12 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-[#003366]" strokeWidth={2} />
              </div>
              <div>
                <h4 className="font-bold text-slate-400 text-[10px] uppercase tracking-widest mb-1">Email Support</h4>
                <p className="text-[#0a192f] font-bold text-lg tracking-tight">hello@gosmoothroute.info</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="lg:col-span-7">
          <ContactForm initialPlan={initialPlan} />
        </div>
      </div>
    </section>
  );
};

const RouteBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
  { text: "Hello! I'm RouteBot, your specialized moving assistant. How can I help you today?", isBot: true }
]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const messagesEndRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => scrollToBottom(), [messages, isLoading, selectedImage]);
  
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setSelectedImage(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    const textToSend = input.trim();
    const currentImage = selectedImage;
    
    if ((!textToSend && !currentImage) || isLoading) return;

    setMessages(prev => [...prev, { text: textToSend, isBot: false, image: currentImage }]);
    setInput('');
    setSelectedImage(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: textToSend || "User sent an image." })
      });

      const data = await response.json();
      
      if (response.ok && data.reply) {
        setMessages(prev => [...prev, { text: data.reply, isBot: true }]);
      } else {
        throw new Error(data.error || 'Invalid response from API');
      }
    } catch (error) {
      console.error("Error:", error);
      setMessages(prev => [...prev, { 
        text: "Connection failed. Retrying protocol.", 
        isBot: true 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.98 }} transition={{ duration: 0.15 }} className="fixed bottom-24 right-6 w-80 sm:w-96 bg-white border border-slate-300 shadow-2xl rounded-xl overflow-hidden z-50 flex flex-col h-[500px]">
            <div className="bg-[#0a192f] text-white p-4 flex justify-between items-center z-10 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="bg-[#14b8a6]/20 p-2 rounded-md border border-[#14b8a6]/30"><Bot className="w-4 h-4 text-[#14b8a6]" strokeWidth={2} /></div>
                <div><h4 className="font-bold tracking-tight text-xs uppercase">RouteBot</h4><p className="text-[9px] text-slate-400 tracking-widest uppercase">Live Support</p></div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white transition-colors"><Minus className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50 relative">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex w-full mb-4 ${msg.isBot ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[85%] p-3 rounded-lg text-sm leading-relaxed border ${msg.isBot ? 'bg-white border-slate-200 text-slate-700' : 'bg-[#003366] border-[#002244] text-white'}`}>
                  {msg.image && <img src={msg.image} alt="Uploaded" className="max-w-full h-auto rounded-md mb-2 object-cover border border-slate-700" />}
                  {msg.isBot ? (
                    <div className="prose prose-sm max-w-none text-slate-700 prose-headings:text-[#0a192f] prose-a:text-[#003366]">
                      <ReactMarkdown>{msg.text}</ReactMarkdown>
                    </div>
                  ) : (
                    msg.text
                  )}
                </div>
              </div>
            ))}
              {isLoading && <div className="flex justify-start"><div className="bg-white border border-slate-200 text-slate-500 p-3 rounded-lg shadow-sm flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin text-[#14b8a6]" /><span className="text-[10px] uppercase tracking-widest font-bold">Typing...</span></div></div>}
              <div ref={messagesEndRef} />
            </div>
            <div className="bg-white border-t border-slate-200 flex flex-col relative p-4">
              {selectedImage && <div className="relative inline-block self-start mb-3"><img src={selectedImage} alt="Preview" className="h-16 w-16 object-cover rounded-md border border-slate-200 shadow-sm" /><button type="button" onClick={() => setSelectedImage(null)} className="absolute -top-2 -right-2 bg-slate-900 text-white rounded-full p-1 hover:bg-slate-700 transition-colors shadow-md"><X className="w-3 h-3" /></button></div>}
              <form onSubmit={handleSend} className="flex gap-2 items-end">
                <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageUpload} className="hidden" />
                <button type="button" onClick={() => fileInputRef.current?.click()} className="text-slate-400 hover:text-[#003366] p-2 mb-0.5 transition-colors shrink-0"><Paperclip className="w-5 h-5" strokeWidth={2} /></button>
                <textarea value={input} onChange={(e) => { setInput(e.target.value); e.target.style.height = '40px'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); e.target.style.height = '40px'; } }} placeholder="Ask me anything..." className="flex-1 bg-slate-100 border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#003366] transition-all resize-none overflow-y-auto font-mono placeholder:font-sans" style={{ minHeight: '40px', height: '40px' }} />
                <button type="submit" disabled={isLoading || (!input.trim() && !selectedImage)} className="bg-[#0a192f] text-white p-2.5 mb-0.5 rounded-md disabled:opacity-50 hover:bg-[#003366] transition-colors flex items-center justify-center shrink-0"><Send className="w-4 h-4 ml-0.5" strokeWidth={2} /></button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setIsOpen(!isOpen)} className="fixed bottom-6 right-6 bg-[#0a192f] text-white p-4 rounded-xl shadow-xl z-50 flex items-center justify-center hover:bg-[#003366] border border-[#002244] transition-colors">{isOpen ? <X className="w-6 h-6" /> : <Bot className="w-6 h-6" />}</motion.button>
    </>
  );
};

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const handleSubscribe = (e) => { e.preventDefault(); if (email.trim() && email.includes('@')) { setIsSubscribed(true); setEmail(''); setTimeout(() => setIsSubscribed(false), 4000); } };
  
  return (
    <footer className="bg-white text-slate-900 pt-16 md:pt-24 pb-8 border-t border-slate-200 mt-auto">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          <div className="col-span-2 md:col-span-1 flex flex-col">
            <Link to="/" className="h-10 mb-6 flex items-center cursor-pointer">
              <img src="https://res.cloudinary.com/dnhn5r2gn/image/upload/v1774641237/Logo_tra_df0zpc.png" alt="SmoothRoute Logo" className="max-h-full w-auto object-contain origin-left" />
            </Link>
            <p className="text-slate-500 mb-6 font-normal text-sm pr-4 leading-relaxed">Systematic logistics coordination. We construct the timeline, dispatch the vendors, and monitor execution.</p>
          </div>
          <div className="flex flex-col">
            <div className="mb-6"><h4 className="font-bold text-[10px] tracking-widest uppercase text-slate-400">Services</h4></div>
            <ul className="space-y-3 text-slate-700 font-semibold text-sm">
              <li><Link to="/services/residential" className="hover:text-[#003366] transition-colors">Residential Moving</Link></li>
              <li><Link to="/services/office" className="hover:text-[#003366] transition-colors">Office Relocation</Link></li>
              <li><Link to="/services/route" className="hover:text-[#003366] transition-colors">Route Planning</Link></li>
              <li><Link to="/services/vendor" className="hover:text-[#003366] transition-colors">Vendor Booking</Link></li>
            </ul>
          </div>
          <div className="flex flex-col">
            <div className="mb-6"><h4 className="font-bold text-[10px] tracking-widest uppercase text-slate-400">Company</h4></div>
            <ul className="space-y-3 text-slate-700 font-semibold text-sm">
              <li><Link to="/about" className="hover:text-[#003366] transition-colors">Our Story</Link></li>
              <li><Link to="/careers" className="hover:text-[#003366] transition-colors">Careers</Link></li>
              <li><Link to="/faq" className="hover:text-[#003366] transition-colors">FAQ</Link></li>
              <li><Link to="/partner-network" className="hover:text-[#003366] transition-colors">Partner Network</Link></li>
              <li><Link to="/contact" className="hover:text-[#003366] transition-colors">Contact Us</Link></li>
            </ul>
          </div>
          <div className="flex flex-col">
            <div className="mb-6"><h4 className="font-bold text-[10px] tracking-widest uppercase text-slate-400">Newsletter</h4></div>
            <p className="text-slate-500 font-normal mb-4 text-sm">Helpful moving tips and savings sent monthly.</p>
            {isSubscribed ? 
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm text-[#003366] font-bold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#14b8a6]" /> Thanks for subscribing!</motion.div> 
              : 
              <form onSubmit={handleSubscribe} className="flex flex-col gap-2">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white border border-slate-300 rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#003366] transition-colors font-medium text-slate-900" placeholder="Email address" />
                <button type="submit" className="bg-[#0a192f] text-white px-4 py-2.5 rounded-md text-sm font-bold hover:bg-[#003366] transition-colors">Subscribe</button>
              </form>
            }
          </div>
        </div>
        <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest">© 2026 SmoothRoute Logistics LLC. Not a motor carrier.</p>
          <div className="flex gap-6 text-slate-400 font-bold text-[10px] uppercase tracking-widest">
            <Link to="/privacy" className="hover:text-[#003366] transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#003366] transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// --- Animated Route System ---
const AnimatedRoutes = () => {
  const location = useLocation();
  const [history, setHistory] = useState(['/']);

  useEffect(() => {
    window.scrollTo(0, 0);
    setHistory(prev => {
      if (prev[prev.length - 1] === location.pathname) return prev;
      if (prev.length > 1 && prev[prev.length - 2] === location.pathname) {
        return prev.slice(0, -1);
      }
      return [...prev, location.pathname];
    });
  }, [location.pathname]);

  const pageNames = {
    '/': 'Home',
    '/services': 'Services',
    '/pricing': 'Pricing',
    '/about': 'Our Story',
    '/careers': 'Careers',
    '/job': 'Job Details',
    '/apply': 'Application Portal',
    '/faq': 'FAQ',
    '/contact': 'Contact Us',
    '/how-it-works': 'How It Works',
    '/partner-network': 'Partner Network',
    '/partner-portal': 'Partner Portal',
    '/services/residential': 'Residential Moving',
    '/services/office': 'Office Relocation',
    '/services/route': 'Route Planning',
    '/services/vendor': 'Vendor Booking',
    '/privacy': 'Privacy Policy',
    '/terms': 'Terms of Service'
  };

  const prevPath = history.length > 1 ? history[history.length - 2] : null;
  const prevName = prevPath ? pageNames[prevPath] || 'Previous' : '';

  const dynamicBackProps = (history.length > 1 && location.pathname !== '/') 
    ? { backPath: prevPath, backText: prevName } 
    : {};

  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/" element={<PageWrapper><HomePage /></PageWrapper>} />
      <Route path="/how-it-works" element={<PageWrapper {...dynamicBackProps}><HowItWorksPage /></PageWrapper>} />
      <Route path="/services" element={<PageWrapper {...dynamicBackProps}><ServicesPage serviceData={serviceData} /></PageWrapper>} />
      <Route path="/services/residential" element={<PageWrapper {...dynamicBackProps}><ServiceDetailPage {...serviceData.residential} /></PageWrapper>} />
      <Route path="/services/office" element={<PageWrapper {...dynamicBackProps}><ServiceDetailPage {...serviceData.office} /></PageWrapper>} />
      <Route path="/services/route" element={<PageWrapper {...dynamicBackProps}><ServiceDetailPage {...serviceData.route} /></PageWrapper>} />
      <Route path="/services/vendor" element={<PageWrapper {...dynamicBackProps}><ServiceDetailPage {...serviceData.vendor} /></PageWrapper>} />
      <Route path="/partner-network" element={<PageWrapper {...dynamicBackProps}><PartnerNetworkPage /></PageWrapper>} />
      <Route path="/partner-portal" element={<PageWrapper {...dynamicBackProps}><PartnerPortalPage /></PageWrapper>} />
      <Route path="/contact" element={<PageWrapper {...dynamicBackProps}><ContactPage /></PageWrapper>} />
      <Route path="/pricing" element={<PageWrapper {...dynamicBackProps}><Pricing /></PageWrapper>} />
      <Route path="/about" element={<PageWrapper {...dynamicBackProps}><About /></PageWrapper>} />
      <Route path="/careers" element={<PageWrapper {...dynamicBackProps}><CareersPage /></PageWrapper>} />
      <Route path="/job" element={<PageWrapper {...dynamicBackProps}><JobDetailsPage /></PageWrapper>} />
      <Route path="/apply" element={<PageWrapper {...dynamicBackProps}><JobApplicationPage /></PageWrapper>} />
      <Route path="/faq" element={<PageWrapper {...dynamicBackProps}><FAQPage /></PageWrapper>} />
      <Route path="/privacy" element={<PageWrapper {...dynamicBackProps}><TextPage title="Privacy Policy" lastUpdated="March 2026" content={<><p>At SmoothRoute Logistics LLC ("SmoothRoute," "we," "us," or "our"), we respect your privacy and are committed to protecting the personal information you share with us. This Privacy Policy describes how we collect, use, and share your information in connection with our move planning and coordination services.</p><h3>1. Information We Collect</h3><p>To provide professional move coordination, we collect several types of information from and about users of our Services, including:</p><ul><li><strong>Personal Identifiers:</strong> Name, email address, telephone number, and postal addresses (current and destination).</li><li><strong>Logistics Data:</strong> Moving dates, inventory lists, special handling requirements (e.g., piano or artwork details), and budget constraints.</li><li><strong>Technical Data:</strong> IP addresses, browser type, and interaction data collected through cookies to improve our website experience.</li></ul><h3>2. How We Use Your Information</h3><p>We use the information we collect to deliver "Intelligent, Efficient Logistics." This includes:</p><ul><li>Developing custom move plans and route optimizations.</li><li>Verifying and booking services with third-party vendors (movers, truck rentals, etc.).</li><li>Communicating with you regarding your moving schedule and project status.</li><li>Processing payments and managing your service tier subscriptions.</li><li>Improving our internal algorithms for route and cost optimization.</li></ul><h3>3. Information Sharing and Disclosure</h3><p>Because SmoothRoute is a coordination service, sharing your data with vetted third parties is necessary to execute your move:</p><ul><li><strong>Service Vendors:</strong> We share necessary logistics data (address, inventory, timing) with licensed moving companies, truck rental providers, and cleaning services to fulfill your booking.</li><li><strong>Legal Compliance:</strong> We may disclose information if required by law or in response to valid requests by public authorities.</li><li><strong>Business Transfers:</strong> If SmoothRoute is involved in a merger or acquisition, your information may be transferred as a business asset.</li></ul><h3>4. Data Security</h3><p>We implement industry-standard security measures, including SSL/TLS encryption for data in transit and secure access controls for stored data. However, no method of transmission over the Internet is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.</p><h3>5. Your Rights and Choices</h3><p>Depending on your location, you may have the right to access, correct, or delete the personal information we hold about you. You may also opt-out of marketing communications at any time by following the "unsubscribe" link in our emails.</p><h3>6. Third-Party Links</h3><p>Our website may contain links to third-party websites (e.g., U-Haul, Penske). We are not responsible for the privacy practices or content of these external sites.</p><h3>7. Contact Us</h3><p>If you have questions about this Privacy Policy, please contact our data privacy coordinator at <a href="mailto:privacy@gosmoothroute.info">privacy@gosmoothroute.info</a>.</p></>} /></PageWrapper>} />
      <Route path="/terms" element={<PageWrapper {...dynamicBackProps}><TextPage title="Terms of Service" lastUpdated="March 2026" content={<><p>These Terms of Service ("Terms") govern your access to and use of the services provided by SmoothRoute Logistics LLC ("SmoothRoute," "we," "us," or "our"). By engaging our services or using our website, you agree to be bound by these Terms.</p><div className="bg-slate-50 p-6 rounded-xl border border-slate-200 mb-8 mt-6 shadow-sm"><h4 className="mt-0 text-[#003366] font-bold text-lg tracking-tight mb-2">CRITICAL DISCLOSURE: NOT A MOTOR CARRIER</h4><p className="mb-0 text-slate-700 italic text-sm leading-relaxed">SmoothRoute Logistics LLC is a move planning, logistics coordination, and consulting service. We are NOT a motor carrier, we do not own moving trucks, and we do not provide manual moving labor. We act as a professional intermediary that sources, vets, and books third-party licensed motor carriers and service providers on your behalf.</p></div><h3>1. Description of Services</h3><p>SmoothRoute provides move planning, vendor booking, route optimization, and coordination services. Our role is to develop a logistics strategy and manage the administrative aspects of your relocation. The physical transportation of goods is performed exclusively by independent third-party vendors.</p><h3>2. User Responsibilities</h3><p>To ensure a "Smarter Move," you agree to:</p><ul><li>Provide accurate and complete inventory lists and location details.</li><li>Ensure that you have the legal right to move the items listed in your inventory.</li><li>Be present (or provide an authorized representative) during the execution phase of the move.</li><li>Pay all coordination fees to SmoothRoute and all labor/truck fees to the respective vendors as outlined in your Move Plan.</li></ul><h3>3. Payments and Refunds</h3><p>Service fees for SmoothRoute's coordination tiers (Basic, Standard, Premium) are due as specified at the time of booking. Planning fees are non-refundable once the custom Move Plan has been delivered to the client. Third-party vendor fees (movers, cleaners) are governed by the specific terms of those vendors.</p><h3>4. Limitation of Liability</h3><p>SmoothRoute Logistics LLC is not liable for any physical loss, damage, or theft of your personal property during the move. Liability for cargo damage rests solely with the third-party motor carrier performing the physical transport. While we only book vetted and insured vendors, any claims for damages must be filed directly with the carrier. SmoothRoute's total liability for any service-related dispute is limited to the amount paid to us for coordination services.</p><h3>5. Vendor Neutrality and Relationships</h3><p>We provide recommendations based on cost, reliability, and availability. We do not have an employment or agency relationship with the movers or truck rental companies. They are independent contractors, and SmoothRoute does not exercise control over their physical methods of operation.</p><h3>6. Force Majeure</h3><p>SmoothRoute is not responsible for delays or failures in performance resulting from acts beyond our control, including but not limited to: traffic congestion, severe weather, road closures, vendor mechanical failure, or government restrictions.</p><h3>7. Governing Law</h3><p>These Terms shall be governed by and construed in accordance with the laws of the State of Arkansas, without regard to its conflict of law provisions.</p><h3>8. Modifications</h3><p>We reserve the right to modify these Terms at any time. Your continued use of our Services after any changes constitutes your acceptance of the new Terms.</p></>} /></PageWrapper>} />
    </Routes>
  );
};

export default function App() {
  return (
    <HashRouter>
      <div className="min-h-screen bg-[#F8FAFC] font-sans text-slate-900 selection:bg-[#003366] selection:text-white flex flex-col overflow-x-hidden">
        <Navbar />
        <main className="flex-1">
          <AnimatedRoutes />
        </main>
        <Footer />
        <RouteBot />
      </div>
    </HashRouter>
  );
}
